import type { OptionCardView } from './api.js';

/**
 * How much a card starts with, in words.
 *
 * Sixteen Options carry a payload and not one of the printed texts says
 * so: "You may fire the Big Gun instead of your robot's main laser" gives
 * a player no way to learn it has five shells. The tray showed a bare
 * number on the chip and nothing to say what it counted.
 *
 * Generated from the card rather than written into `text`, which is the
 * PRINTED wording and is the rule -- this is a note about the card, and
 * mixing the two would make invented sentences indistinguishable from the
 * ones on the physical card.
 *
 * Empty for a card with no payload, which is most of them, so a caller can
 * append it unconditionally.
 */
export function payloadNote(option: OptionCardView): string {
  if (option.payload === undefined) return '';

  // A one-shot is gone once used, which is the card itself ending and so
  // worth saying. An empty launcher is still held, and where it refills is
  // the chop shop's business, not the card's.
  const fate = option.payloadKind === 'ammo' ? '' : ', discarded when spent';

  // `remaining` is absent on the catalog entry -- the game-setup list,
  // where no robot holds anything yet -- and present on a card a robot is
  // carrying. Named only when it has actually been drawn down, since a
  // full card saying "5 of 5" is noise on every chip in the tray.
  const left = option.remaining !== undefined && option.remaining !== option.payload
    ? `, ${option.remaining} left`
    : '';

  return `Payload: ${option.payload}${left}${fate}.`;
}

/** The card's text with its payload noted after it, for a tooltip. */
export function optionTooltip(option: OptionCardView): string {
  const note = payloadNote(option);
  return note ? `${option.text}\n\n${note}` : option.text;
}
